import React, { useState, useRef, useEffect } from "react";
import { InputBox } from "@/components/ui/InputBox";
import DateOneBox from "@/components/ui/DateOneBox";
import SelectBox from "@/components/ui/SelectBox";
import { Button } from "@/components/ui/Button";
import ModalLayout from "@/components/layout/ModalLayout";
import { toast } from "sonner";
import ModalHeader from "@/components/ui/ModalHeader";
import DateBox from "@/components/ui/DateBox";
import { CheckBox } from "@/components/ui/CheckBox";
import RadioGroup from "@/components/ui/RadioBtn";
import { InsertCompanyCodeRequest, StCompanyCode } from "@/interface/standard/st_company_code";
import { useInsertCompanyCode, useUpdateCompanyCode } from "@/hooks/standard/st_company_code/useCompanyCode";
import { formatDate } from "@/lib/date";

interface AddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: InsertCompanyCodeRequest, mode: "add" | "edit") => void;
  mode: "add" | "edit";
  initialData?: StCompanyCode; // 수정 시 기존 데이터
}

export default function AddModal({ isOpen, onClose, onSave, mode, initialData }: AddModalProps) {
  const [formData, setFormData] = useState<InsertCompanyCodeRequest>({
    stCompanyName: "",
    stCompanyNameshort: "",
    stCompanyNumber: "",
    stCompanyLawnumber: "",
    stCompanyOwnername: "",
    stCompanyDate: new Date(),
    stCompanyTel: "",
    stCompanyFax: "",
    stCompanyMovetel: "",
    stCompanyCondition: "",
    stCompanyItem: "",
    stCompanyTzip: "",
    stCompanyTaddress1: "",
    stCompanyContdatef: new Date(),
    stCompanyContdatet: new Date(),
    stCompanyRemark: "",
    stCompanyType: 1, // 화주사를 기본값으로 설정
  });
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [isContractPeriod, setIsContractPeriod] = useState<boolean>(false);
  const [businessType, setBusinessType] = useState<string>("1"); // 사업자 유형 (1=개인사업자, 2=법인사업자)

  // 필드 refs
  const companyNameRef = useRef<HTMLInputElement>(null);
  const companyNumberRef = useRef<HTMLInputElement>(null);
  const companyLawnumberRef = useRef<HTMLInputElement>(null);
  const companyOwnernameRef = useRef<HTMLInputElement>(null);

  const { mutate: insertCompanyCode } = useInsertCompanyCode();
  const { mutate: updateCompanyCode } = useUpdateCompanyCode();

  // 품목구분 옵션
  const itemCategoryOptions = [
    { value: "1", label: "화주사" },
    { value: "2", label: "운전사" },
  ];

  // 수정 모드일 때 초기 데이터 설정
  useEffect(() => {
    if (mode === "edit" && initialData) {
      setFormData({
        stCompanyName: initialData.stCompanyName || "",
        stCompanyNameshort: initialData.stCompanyNameshort || "",
        stCompanyNumber: initialData.stCompanyNumber || "",
        stCompanyLawnumber: initialData.stCompanyLawnumber || "",
        stCompanyOwnername: initialData.stCompanyOwnername || "",
        stCompanyDate: initialData.stCompanyDate ? new Date(initialData.stCompanyDate) : new Date(),
        stCompanyTel: initialData.stCompanyTel || "",
        stCompanyFax: initialData.stCompanyFax || "",
        stCompanyMovetel: initialData.stCompanyMovetel || "",
        stCompanyCondition: initialData.stCompanyCondition || "",
        stCompanyItem: initialData.stCompanyItem || "",
        stCompanyTzip: initialData.stCompanyTzip || "",
        stCompanyTaddress1: initialData.stCompanyTaddress1 || "",
        stCompanyContdatef: initialData.stCompanyContdatef ? new Date(initialData.stCompanyContdatef) : new Date(),
        stCompanyContdatet: initialData.stCompanyContdatet ? new Date(initialData.stCompanyContdatet) : new Date(),
        stCompanyRemark: initialData.stCompanyRemark || "",
        stCompanyType: initialData.stCompanyType || 1,
      });
    }
  }, [mode, initialData]);

  const handleInputChange = (field: keyof InsertCompanyCodeRequest, value: string | number | Date) => {
    setFormData(prev => {
      // 날짜 필드들은 Date 객체로 변환
      if (field === "stCompanyDate" || field === "stCompanyContdatef" || field === "stCompanyContdatet") {
        return { ...prev, [field]: new Date(value as string) };
      }
      // stCompanyType은 number 타입
      if (field === "stCompanyType") {
        return { ...prev, [field]: value as number };
      }
      return { ...prev, [field]: value as string };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 필수 필드 검증
    if (!formData.stCompanyName.trim()) {
      toast.warning("업체명을 입력해주세요.");
      companyNameRef.current?.focus();
      return;
    }

    if (isContractPeriod) {
      if (!startDate.trim() || !endDate.trim()) {
        toast.warning("계약기간을 선택해주세요.");
        return;
      }
    }

    if (businessType === "1") {
      if (!formData.stCompanyNumber.trim()) {
        toast.warning("사업자 등록 번호를 입력해주세요.");
        companyNumberRef.current?.focus();
        return;
      }

      if (formData.stCompanyNumber.length !== 10) {
        toast.warning("사업자 등록 번호는 -를 제외한 10자리여야 합니다.");
        companyNumberRef.current?.focus();
        return;
      }
    }

    if (businessType === "2") {
      if (!formData.stCompanyNumber.trim()) {
        toast.warning("사업자 등록 번호를 입력해주세요.");
        companyNumberRef.current?.focus();
        return;
      }

      if (formData.stCompanyNumber.length !== 10) {
        toast.warning("사업자 등록 번호는 -를 제외한 10자리여야 합니다.");
        companyNumberRef.current?.focus();
        return;
      }

      if (!formData.stCompanyLawnumber.trim()) {
        toast.warning("법인 번호를 입력해주세요.");
        companyLawnumberRef.current?.focus();
        return;
      }

      if (formData.stCompanyLawnumber.length !== 13) {
        toast.warning("법인 번호는 -를 제외한 13자리여야 합니다.");
        companyLawnumberRef.current?.focus();
        return;
      }
    }

    if (formData.stCompanyOwnername === "") {
      toast.warning("대표자를 입력해주세요.");
      companyOwnernameRef.current?.focus();
      return;
    }

    if (!formData.stCompanyDate || isNaN(formData.stCompanyDate.getTime())) {
      toast.warning("개업일자를 선택해주세요.");
      // 개업일자 섹션으로 스크롤
      document.querySelector('[data-section="개업일자"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    // if (formData.stCompanyTzip.length !== 5) {
    //   toast.warning("우편번호 5자리를 입력해주세요.");
    //   return;
    // }

    // API 호출로 데이터 저장
    if (mode === "add") {
      insertCompanyCode(formData, {
      onSuccess: (res) => {
        const payload = res?.data ?? formData;
        onSave(payload, mode); // 부모 컴포넌트에 성공 알림
        handleCancel();
        onClose();
      },
      });
    } else {
      // 수정 모드일 때는 initialData의 ID를 사용
      if (initialData?.stCompanyCode) {
        console.log(initialData.stCompanyCode);
        console.log(formData);
        updateCompanyCode(
          { id: initialData.stCompanyCode, data: formData },
          {
            onSuccess: (res) => {
              const payload = res?.data ?? formData;
              onSave(payload, mode);
              handleCancel();
              onClose();
            },
          }
        );
      }
    }
  };

  const handleCancel = () => {
    // 폼 데이터 초기화
    if (mode === "add") {
      setFormData({
        stCompanyName: "",
        stCompanyNameshort: "",
        stCompanyNumber: "",
        stCompanyLawnumber: "",
        stCompanyOwnername: "",
        stCompanyDate: new Date(),
        stCompanyTel: "",
        stCompanyFax: "",
        stCompanyMovetel: "",
        stCompanyCondition: "",
        stCompanyItem: "",
        stCompanyTzip: "",
        stCompanyTaddress1: "",
        stCompanyContdatef: new Date(),
        stCompanyContdatet: new Date(),
        stCompanyRemark: "",
        stCompanyType: 1, // 화주사를 기본값으로 설정
      });
      // 로컬 상태 초기화
      setBusinessType("1");
      setStartDate("");
      setEndDate("");
      setIsContractPeriod(false);
    }
    onClose();
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} title={mode === "add" ? "업체코드 추가" : "업체코드 수정"}>
      <form onSubmit={handleSubmit}>
        {/* 폼 내용 */}
        <ModalHeader title="기본 정보" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {/* 첫 번째 행 */}
          <div className="space-y-4">
            <InputBox
              ref={companyNameRef}
              required={true}
              label="업체명"
              value={formData.stCompanyName}
              onChange={e => handleInputChange("stCompanyName", e.target.value)}
              placeholder="입력"
              className="w-full"
            />
          </div>

          <div className="space-y-4">
            <InputBox
              label="줄임상호"
              value={formData.stCompanyNameshort}
              onChange={e => handleInputChange("stCompanyNameshort", e.target.value)}
              placeholder="입력"
              className="w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="space-y-4">
            <SelectBox
              required={true}
              label="구분"
              value={formData.stCompanyType.toString()}
              onChange={e => handleInputChange("stCompanyType", parseInt(e))}
              placeholder="선택"
              options={itemCategoryOptions}
            />
          </div>
          <div>
            <CheckBox
              label="계약기간"
              checked={isContractPeriod}
              onChange={e => setIsContractPeriod(e.target.checked)}
            />
            <div className="mb-[6px]" />
            <DateBox
              // label="등록일자"
              disabled={!isContractPeriod}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={date => {
                setStartDate(date);
                if (date) {
                  const startDateObj = new Date(date);
                  handleInputChange("stCompanyContdatef", startDateObj);
                }
              }}
              onEndDateChange={date => {
                setEndDate(date);
                if (date) {
                  const endDateObj = new Date(date);
                  handleInputChange("stCompanyContdatet", endDateObj);
                }
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-3 mb-4">
          <div className="space-y-4">
            <InputBox
              label="비고"
              value={formData.stCompanyRemark}
              onChange={e => handleInputChange("stCompanyRemark", e.target.value)}
              placeholder="입력"
              className="w-full"
            />
          </div>
        </div>

        <ModalHeader title="사업자 정보" />
        <div className="grid grid-cols-1 md:grid-cols-1 gap-3 mb-4">
          <div className="space-y-4">
            <RadioGroup
              required={true}
              label="사업자 유형"
              name="userType"
              value={businessType}
              onChange={e => setBusinessType(e)}
              options={[
                { value: "1", label: "개인사업자" },
                { value: "2", label: "법인사업자" },
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="space-y-4">
            <InputBox
              ref={companyNumberRef}
              required
              label="사업자 등록 번호"
              placeholder="검색"
              showIcon={true}
              value={formData.stCompanyNumber}
              iconSrc="/icons/searchBlack.svg"
              onChange={e => handleInputChange("stCompanyNumber", e.target.value)}
              onIconClick={() => {
                console.log("검색");
              }}
            />
          </div>

          <div className="space-y-4">
            <InputBox
              ref={companyLawnumberRef}
              required={businessType === "2"}
              disabled={businessType === "1"}
              label="법인 번호"
              placeholder="검색"
              showIcon={true}
              value={formData.stCompanyLawnumber}
              iconSrc="/icons/searchBlack.svg"
              onChange={e => handleInputChange("stCompanyLawnumber", e.target.value)}
              onIconClick={() => {
                console.log("검색");
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="space-y-4">
            <InputBox
              ref={companyOwnernameRef}
              required
              label="대표자"
              placeholder="입력"
              value={formData.stCompanyOwnername}
              onChange={e => handleInputChange("stCompanyOwnername", e.target.value)}
            />
          </div>
          <div className="space-y-4">
            <DateOneBox
              required
              label="개업일자"
              date={formatDate(formData.stCompanyDate)}
              onDateChange={e => {
                // 문자열을 Date 객체로 변환
                const date = new Date(e);
                setFormData(prev => ({ ...prev, stCompanyDate: date }));
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-[6px] mb-4">
          <div className="col-span-1">
            <InputBox
              label="주소"
              placeholder="우편번호"
              value={formData.stCompanyTzip}
              onChange={e => handleInputChange("stCompanyTzip", e.target.value)}
            />
          </div>
          <div className="col-span-3">
            <InputBox
              label=""
              placeholder="주소 입력"
              value={formData.stCompanyTaddress1}
              onChange={e => handleInputChange("stCompanyTaddress1", e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-4">
          <div className="space-y-4">
            <InputBox
              label="업태"
              placeholder="입력"
              value={formData.stCompanyCondition}
              onChange={e => handleInputChange("stCompanyCondition", e.target.value)}
            />
          </div>
          <div className="space-y-4">
            <InputBox
              label="종목"
              placeholder="입력"
              value={formData.stCompanyItem}
              onChange={e => handleInputChange("stCompanyItem", e.target.value)}
            />
          </div>
        </div>

        <ModalHeader title="연락처 정보" />
        <div className="grid grid-cols-3 md:grid-cols-3 gap-3">
          <div className="space-y-4">
            <InputBox
              label="전화번호"
              placeholder="입력"
              value={formData.stCompanyTel}
              onChange={e => handleInputChange("stCompanyTel", e.target.value)}
            />
          </div>
          <div className="space-y-4">
            <InputBox
              label="휴대전화"
              placeholder="입력"
              value={formData.stCompanyMovetel}
              onChange={e => handleInputChange("stCompanyMovetel", e.target.value)}
            />
          </div>
          <div className="space-y-4">
            <InputBox
              label="팩스"
              placeholder="입력"
              value={formData.stCompanyFax}
              onChange={e => handleInputChange("stCompanyFax", e.target.value)}
            />
          </div>
        </div>

        {/* 모달 푸터 */}
        <div className="flex items-center gap-3 mt-6">
          <div className="flex justify-start w-full">
            <Button type="button" variant="white" onClick={handleCancel} className="py-[10px]">
              취소
            </Button>
          </div>
          <div className="flex justify-end w-full">
            <Button type="button" variant="primary" className="py-[10px]" onClick={handleSubmit}>
              {mode === "add" ? "추가" : "수정"}
            </Button>
          </div>
        </div>
      </form>
    </ModalLayout>
  );
}
